import type { CoachJourneyInput, CoachTransitionInput } from '../schema';

const PROJECT = ['boardId', 'projectNodeId'];
const CREATED: CoachTransitionInput = {
  event: 'project.created',
  match: ['boardId'],
  bind: ['projectNodeId'],
  if: { source: 'copilot' },
  fresh: true,
  to: 'script',
};
const SAVED: CoachTransitionInput = {
  event: 'project.take-saved',
  match: PROJECT,
  to: 'edit',
};
const OUTCOMES: CoachTransitionInput[] = [
  { event: 'project.planned', match: PROJECT, to: 'planned' },
  { event: 'post.accepted', match: PROJECT, if: { outcome: 'scheduled' }, to: 'scheduled' },
  { event: 'post.accepted', match: PROJECT, if: { outcome: 'published' }, to: 'published' },
  { event: 'post.accepted', match: PROJECT, if: { outcome: 'publishing' }, to: 'publishing' },
];
export const FILM: CoachTransitionInput[] = [
  SAVED,
  { event: 'studio.camera-chosen', match: PROJECT, to: 'filming' },
  { event: 'studio.upload-chosen', match: PROJECT, to: 'filming' },
];
const POST_BODY =
  'Choose where it goes, then press the button. Nothing goes out until you do. No account yet? Choose Plan to save it to your calendar.';
/** Ready answers for the niche question: tap one, then make it yours. */
export const NICHE_SUGGESTIONS = [
  { label: '💪 Fitness', text: 'I help busy people get fit and strong at home.' },
  { label: '🍳 Food', text: 'I share quick, easy meals for busy weeknights.' },
  { label: '🏪 Local business', text: 'I run a local business and want more people to find us.' },
  { label: '💼 Freelancing', text: 'I help freelancers find clients and get paid on time.' },
  { label: '💸 Money', text: 'I help people save money and get out of debt.' },
  { label: '💄 Beauty', text: 'I share simple makeup and skincare tips.' },
  { label: '🏡 Real estate', text: 'I help first-time buyers find and buy a home.' },
  { label: '🧠 Coaching', text: 'I coach people who want to change careers.' },
];
/** The tour's own card in the Copilot conversation, bright while the rest of the screen dims. */
export const QUESTION = [{ anchor: 'coach.question' as const }];
const REOPEN = {
  anchor: 'project.open-studio' as const,
  when: { studio: 'closed' as const },
  title: 'Your video is right here 🎬',
  body: 'Open your project to keep going. Your script, saved take, and edits stay together.',
};

/**
 * A real first video, made with Copilot. The tour speaks only when it is the person's move, and
 * each screen has one next step. Only saved plans and accepted posts reach success.
 */
export const contentStudioJourney = {
  id: 'content-studio',
  version: 4,
  name: 'Make your first video with Poppy',
  mode: 'guided',
  description: 'Create a script you love, film it, make a few simple edits, then post or plan it.',
  gate: { flag: 'scriptShootPost' },
  // Offered once to everyone it is for, on the first board they open (Start or Not now).
  start: { link: true, auto: 'invite' },
  tutorial: {
    description:
      'From your niche to your first finished video. Poppy walks with you the whole way.',
    on: 'new-board',
  },
  steps: [
    {
      id: 'open',
      display: 'hidden',
      title: 'Your first video starts here 🎬',
      body: 'Open a board you can edit and we’ll make something together.',
      on: [{ event: 'board.entered', match: ['boardId'], bind: ['boardId'], to: 'welcome' }],
    },
    {
      id: 'welcome',
      chapter: 1,
      display: 'conversation',
      title: 'Make your first post with Poppy 🎬',
      body: 'Poppy finds what’s working, writes your script, and you film it. Copilot uses your Poppy credits.',
      show: QUESTION,
      action: { label: 'Let’s make my video', to: 'brief' },
    },
    {
      id: 'brief',
      chapter: 1,
      display: 'conversation',
      title: 'What do you make content about? ✨',
      body: 'Your niche, your business, or just what you know. Tap one to start, then make it yours.',
      show: QUESTION,
      form: {
        id: 'text',
        bind: 'brief',
        submit: 'Find examples',
        placeholder: 'I help new freelancers get paid on time.',
        suggestions: NICHE_SUGGESTIONS,
        to: 'find-examples',
      },
    },
    {
      id: 'find-examples',
      chapter: 1,
      display: 'conversation',
      resumeAt: 'prompt',
      title: 'Finding examples',
      body: 'Poppy is looking at what’s working in your niche.',
      do: {
        action: 'copilot.ask',
        params: {
          label: '{{brief}}',
          prompt:
            'What I make content about: {{brief}}. Find what’s working in this niche: search Poppy’s Discover library with research_posts using short, focused queries. If my answer is vague, search the closest broad topic. Show the actual result cards here in the chat and say in at most two sentences what the best posts have in common. Never invent examples, metrics, rankings or claims. Do not ask me anything, do not add anything to the board, do not open Discover, and do not write the script yet. If nothing useful is found, say so in one sentence.',
        },
      },
      on: [{ event: 'copilot.sent', match: ['boardId'], to: 'examples-working' }],
    },
    {
      id: 'examples-working',
      chapter: 1,
      display: 'conversation',
      resumeAt: 'prompt',
      title: 'Finding examples',
      body: 'Poppy is looking at what’s working in your niche.',
      on: [
        { event: 'copilot.replied', match: ['boardId'], to: 'prompt' },
        { event: 'copilot.failed', match: ['boardId'], to: 'examples-failed' },
      ],
    },
    {
      id: 'examples-failed',
      chapter: 1,
      display: 'conversation',
      title: 'Your research didn’t come through',
      body: 'Nothing is lost. Let’s run it again.',
      show: QUESTION,
      action: { label: 'Try again', to: 'find-examples' },
    },
    {
      id: 'prompt',
      chapter: 1,
      display: 'conversation',
      resumeAt: 'prompt',
      title: 'Now turn these winners into your script ✍️',
      body: 'Your message is ready. Press send and Poppy writes it. You can ask for this anytime you find something you love.',
      // The card and the message box stay bright: sending is the one thing to do.
      show: [{ anchor: 'copilot.input' }],
      do: {
        action: 'copilot.fill',
        params: {
          prompt: 'Write my script based on the winners in this research.',
          context: {
            task: 'create-video-script',
            brief: '{{brief}}',
            instructions:
              'I have not chosen a topic. Choosing it is your job, so never ask me what the video should be about. Pick the one video idea that fits my niche best, based on the examples you just showed me, or on my niche alone if there were none. Create one ready-to-film 20–40 second talking-to-camera video. Use relevant brand context from this board. A specific hook and a body with one concrete payoff and a natural ending. Include a call to action in the body only when it fits. Teach something generally useful in my niche. Never invent personal stories, results, prices or claims about me. No required B-roll. Ask one focused question only if you cannot tell my niche at all. Save one video post with the full script and separate ready-to-edit post text. Use the examples you just showed me for structure only, never their words. Confirm in one short sentence that it is on my board, and do not explain the layout. Do not start more research, publish, or schedule.',
          },
        },
      },
      on: [CREATED, { event: 'copilot.sent', match: ['boardId'], to: 'writing' }],
    },
    {
      id: 'writing',
      chapter: 1,
      display: 'conversation',
      title: 'Writing your script',
      body: 'Poppy is writing your script.',
      resumeAt: 'script-reply',
      on: [
        CREATED,
        { event: 'copilot.replied', match: ['boardId'], to: 'script-reply' },
        {
          event: 'copilot.failed',
          match: ['boardId'],
          bindAs: { message: 'copilotFailure' },
          to: 'script-error',
        },
      ],
    },
    {
      id: 'script-reply',
      chapter: 1,
      display: 'conversation',
      title: 'Poppy needs one thing from you',
      body: 'Answer below. A rough answer is enough.',
      show: [{ anchor: 'copilot.input' }],
      do: { action: 'copilot.open' },
      on: [CREATED, { event: 'copilot.sent', match: ['boardId'], to: 'writing' }],
    },
    {
      id: 'script-error',
      chapter: 1,
      display: 'conversation',
      title: 'Your script didn’t come through',
      body: '{{copilotFailure}} Nothing is lost.',
      show: QUESTION,
      action: { label: 'Try again', to: 'prompt' },
      on: [CREATED, { event: 'copilot.sent', match: ['boardId'], to: 'writing' }],
    },
    {
      id: 'script',
      chapter: 1,
      title: 'Your script is on your board',
      body: 'Click Open to read it and film it.',
      show: [{ anchor: 'project.open-studio', when: { studio: 'closed' } }],
      focus: { node: 'projectNodeId', placement: 'right' },
      on: [{ event: 'post.opened', match: PROJECT, to: 'record' }, ...FILM],
    },
    {
      id: 'record',
      chapter: 2,
      title: 'Here’s your script 🎬',
      body: 'Read it out loud. Want to change a word? Click it and type. Then click Record, or Upload video if you already filmed it.',
      do: {
        action: 'project.open',
        params: { project: 'projectNodeId' },
        onError: 'recover-project',
      },
      show: [
        {
          anchor: 'studio.recording-entry',
          when: { studio: 'open' },
          title: 'Time to film 🎬',
          body: 'Choose your computer or your phone. Your script stays with you while you film.',
        },
        { anchor: 'studio.media', when: { studio: 'open' } },
        REOPEN,
      ],
      focus: { node: 'projectNodeId' },
      on: FILM,
    },
    {
      id: 'recover-project',
      chapter: 2,
      title: 'Let’s reopen your post',
      body: 'Finish any recording or upload, then try again. If the post was removed, Poppy can write a new script.',
      choices: [
        { label: 'Try opening my post', to: 'script' },
        { label: 'Make a new script', to: 'prompt' },
      ],
    },
    {
      id: 'filming',
      chapter: 2,
      display: 'toast',
      title: 'You’ve got this 🎬',
      body: 'Save your take and I’ll meet you in the editor ✨',
      show: [
        {
          when: { studio: 'closed' },
          body: 'Your script is safe. Open your project to finish filming and save your take 🎬',
        },
      ],
      focus: { node: 'projectNodeId' },
      on: [SAVED],
    },
    {
      id: 'edit',
      chapter: 3,
      title: 'That’s YOU on screen 🔥',
      body: 'Play it back. Trim the edges or add captions if you like. When it looks good, click Done editing.',
      do: {
        action: 'project.open',
        params: { project: 'projectNodeId', view: 'video' },
        onError: 'recover-project',
      },
      show: [
        { anchor: 'studio.done-editing', when: { studio: 'open' } },
        {
          anchor: 'studio.edit-media',
          when: { studio: 'open' },
          body: 'Click Edit video to play it back and trim it.',
        },
        REOPEN,
      ],
      focus: { node: 'projectNodeId' },
      on: [{ event: 'studio.editing-done', match: PROJECT, to: 'post-entry' }],
    },
    {
      id: 'return-to-post',
      chapter: 4,
      title: 'Your post is waiting',
      body: 'Finish any recording or upload, then open your post.',
      show: [{ anchor: 'studio.back-to-post', when: { studio: 'open' } }, REOPEN],
      action: { label: 'Open my post', to: 'post-entry' },
      on: [{ event: 'post.opened', match: PROJECT, to: 'post-dialog' }],
    },
    {
      id: 'post-entry',
      chapter: 4,
      title: 'Last step: share it 🚀',
      body: POST_BODY,
      do: {
        action: 'project.open',
        params: { project: 'projectNodeId', view: 'post', delivery: 'post' },
        then: 'post-dialog',
        onError: 'return-to-post',
      },
      show: [{ anchor: 'studio.delivery', when: { studio: 'open' } }, REOPEN],
      focus: { node: 'projectNodeId' },
      on: [{ event: 'post.opened', match: PROJECT, to: 'post-dialog' }, ...OUTCOMES],
    },
    {
      id: 'post-dialog',
      resumeAt: 'post-entry',
      chapter: 4,
      display: 'inline',
      slot: 'post-dialog',
      title: 'Last step: share it 🚀',
      body: POST_BODY,
      // The card is drawn in the post itself; the rest of the screen dims around where it goes.
      show: [{ anchor: 'studio.delivery', when: { studio: 'open' } }],
      on: [...OUTCOMES, { event: 'post.closed', match: PROJECT, to: 'return-to-post' }],
    },
    {
      id: 'planned',
      chapter: 4,
      title: 'Your video has a date 📅',
      body: 'Your plan is saved! Your script, take, and edits stay together. When you’re ready, open it from the calendar to connect an account and post. Nothing has been published ✨',
      do: { action: 'app.celebrate', then: 'calendar-entry' },
      action: { label: 'See my calendar', to: 'calendar-entry' },
    },
    {
      id: 'scheduled',
      chapter: 4,
      title: 'Your video is scheduled 🎉',
      body: 'Your channels accepted the schedule. You took an idea, made it yours, filmed it, and gave it a date. Let’s find it on your calendar 🔥',
      do: { action: 'app.celebrate', then: 'calendar-entry' },
      action: { label: 'See my calendar', to: 'calendar-entry' },
    },
    {
      id: 'published',
      chapter: 4,
      title: 'You just posted YOUR video 🎉',
      body: 'From an idea to a published video. You made that! Next time, bring Poppy another idea and ask for a script. Your project is saved in Content Studio 🔥',
      do: { action: 'app.celebrate', then: 'calendar-entry' },
      action: { label: 'Finish walkthrough', to: 'end' },
    },
    {
      id: 'publishing',
      chapter: 4,
      title: 'Your video is on its way 🚀',
      body: 'Your channels accepted the post and are finishing publishing it. Check its status in Content Studio. Your first video is made, saved, and on its way out into the world ✨',
      do: { action: 'app.celebrate', then: 'calendar-entry' },
      action: { label: 'Finish walkthrough', to: 'end' },
    },
    {
      id: 'calendar-entry',
      display: 'toast',
      title: 'Opening your calendar 📅',
      body: 'Finding your content calendar ✨',
      resumeAt: 'calendar-entry',
      do: { action: 'studio.calendar', then: 'calendar' },
    },
    {
      id: 'calendar',
      resumeAt: 'calendar-entry',
      chapter: 4,
      display: 'inline',
      slot: 'studio-calendar',
      title: 'That’s your first video 🎉',
      body: 'Everything you post or plan shows up on this calendar. Start your next one from Studio.',
      action: { label: 'Done', to: 'end' },
    },
  ],
} satisfies CoachJourneyInput;
